import { MemberObject } from "./MemberUtils"
import { PiMS } from "./PimsUtils"

import { SessionObject } from "@neum/frontend-shared/types/sessionTypes"

export type { SessionStatus, PreHistoryMetadata, PatientDetails, IntegrationMetadata, SessionObject } from "@neum/frontend-shared/types/sessionTypes"

export interface AuditLogs {
    item_id:string,
    sub_item_id?:string,
    item_type:string,
    action: string,
    actor:string,
    timestamp:number,
    metadata: { comment?: string },
    is_impersonation:boolean
}

export interface RecordingTranscriptPair {
    recording_id: string;
    recording_timestamp?: string;
    recording_url?: string;
    transcript_id?: string;
    transcript_status: string;
    transcript_title?: string;
    transcript_content: string;
}

export enum SessionStateEnum {
    ALL = "All",
    NOT_COMPLETED = "Not Completed",
    COMPLETED = "Completed",
}

export enum DocumentProcessType {
    CREATE = "create",
    ADD_REPROCESS = "add_reprocess",
    REPROCESS = "reprocess",
}

export enum DocumentProcessCommand {
    PROCESS_SPEC = "process_spec",
    PROCESS_ALL_REQUIRED = "process_all_required"
}

export function displaySessionsInOrder(sessions: SessionObject[]) {
    const nonCancelledSession = sessions.filter((session) => session.status.recording !== "Cancelled")
    const sortedSessions = nonCancelledSession.sort((a, b) => b.created_timestamp - a.created_timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000); // 24 hours in milliseconds
    
    // Calculate the start of this week (assuming the week starts on Sunday)
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - today.getDay());
    
    // Calculate the start of last week
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);

    // Calculate the start of this month
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const buckets: SessionObject[]  = []

    sortedSessions.forEach(session => {
        const sessionDate = new Date(session.created_timestamp * 1000); // Convert Unix timestamp to JavaScript Date object
        if (sessionDate >= today) {
            buckets.push(session);
        } else if (sessionDate >= yesterday && sessionDate < today) {
            buckets.push(session);
        } else if (sessionDate >= thisWeekStart && sessionDate < today) {
            buckets.push(session);
        } else if (sessionDate >= lastWeekStart && sessionDate < thisWeekStart) {
            buckets.push(session);
        } else if (sessionDate >= thisMonthStart) {
            buckets.push(session);
        } else {
            buckets.push(session);
        }
    });

    return buckets;
}

export function filterCancelledSessions(sessions:SessionObject[]){
    const nonCancelledSession = sessions.filter((session) => session.status.general !== "Cancelled")
    return nonCancelledSession
}

/**
 * Optional filtering parameters specific to on‑prem integrations.
 */
export interface OnPremFilterOptions {
    /**
     * Staff ID defined in org metadata
     */
    staffId?: string;
    /**
     * Feature flag indicating if sync with on‑prem PIMS is enabled for the org.
     */
    syncOnPremPimsEnabled?: boolean;
    /**
     * Value of user.pims – should equal "on-prem-cornerstone" to apply filtering.
     */
    pims?: string;
}

export function filterSessions(
    sessions: SessionObject[],
    userSearch: MemberObject | undefined,
    sortLatest:boolean,
    filterState: SessionStateEnum,
    options: OnPremFilterOptions = {}
) {

    let matchedSession = sessions.filter(session => {
        // Check if userSearch is provided and not equal to "All doctors"
        const isUserSearchValid = userSearch && userSearch.identifier.toLowerCase() !== "all doctors";
        const displayOwnerMatch = isUserSearchValid && session.display_owner ? (session.display_owner.toLowerCase().includes(userSearch.identifier.toLowerCase())) : false;
        const ownerMatch = isUserSearchValid && ((!session.display_owner ? session.owner.toLowerCase().includes(userSearch.identifier.toLowerCase()) : false) || (userSearch.clerk_id ? session.owner_id?.toLowerCase().includes(userSearch.clerk_id.toLowerCase()) : false));
        const matchesUserSearch = isUserSearchValid ? (displayOwnerMatch || ownerMatch): true;
        return matchesUserSearch;
    });

   
    if (
        options.syncOnPremPimsEnabled &&
        options.pims?.toLowerCase() === PiMS.Cornerstone &&
        options.staffId
    ) {
        const staffIdLower = options.staffId.toLowerCase();
        matchedSession = matchedSession.filter(session => {
            const providerId = session.integration_metadata?.provider_id;
            const integrationType = session.integration_metadata?.integration_type;
            if (!providerId || integrationType !== PiMS.Cornerstone) return true;
            return providerId.toLowerCase().includes(`resources/staff_${staffIdLower}`);
        });
    }

    if (filterState === SessionStateEnum.NOT_COMPLETED) {
        matchedSession = matchedSession.filter(session => session.status.general?.toLowerCase() !== "completed");
    } else if (filterState === SessionStateEnum.COMPLETED) {
        matchedSession = matchedSession.filter(session => session.status.general?.toLowerCase() === "completed");
    }

    if (sortLatest) {
        return matchedSession
    } else {
        return matchedSession.sort((a, b) => a.created_timestamp - b.created_timestamp);
    }
}

export function displaySessionsInOrderInBuckets(sessions: SessionObject[]) {
    const sortedSessions = sessions.sort((a, b) => b.created_timestamp - a.created_timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000); // 24 hours in milliseconds
    
    // Calculate the start of this week (assuming the week starts on Sunday)
    const thisWeekStart = new Date(today);
    thisWeekStart.setDate(today.getDate() - today.getDay());
    
    // Calculate the start of last week
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);

    // Calculate the start of this month
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const buckets: { [key: string]: SessionObject[] } = {
        today: [],
        yesterday: [],
        thisWeek: [],
        lastWeek: [],
        thisMonth: [],
        catchAll: []
    };

    sortedSessions.forEach(session => {
        const sessionDate = new Date(session.created_timestamp * 1000); // Convert Unix timestamp to JavaScript Date object
        if (sessionDate >= today) {
            buckets.today.push(session);
        } else if (sessionDate >= yesterday && sessionDate < today) {
            buckets.yesterday.push(session);
        } else if (sessionDate >= thisWeekStart && sessionDate < today) {
            buckets.thisWeek.push(session);
        } else if (sessionDate >= lastWeekStart && sessionDate < thisWeekStart) {
            buckets.lastWeek.push(session);
        } else if (sessionDate >= thisMonthStart) {
            buckets.thisMonth.push(session);
        } else {
            buckets.catchAll.push(session);
        }
    });

    return buckets;
}
